/**
 * Copy for the payment record (S-09, PE-1019), behind `erp_app_proof_of_payment_ingestion_v1`.
 *
 * Its own file rather than more keys under `accounts.paymentLog`: that namespace is the flag-off
 * drawer, which stays exactly as it is until the flag is removed. Mounted as `accounts.paymentRecord`.
 *
 * Context: the drawn strings that carried an em dash are rewritten here as the ticket gives them
 * (in-app copy never carries one), and the undrawn states use the ticket's proposed copy (DL-146).
 *
 * `comparison.captioned` and `attachmentLine` are shared with S-01's upload comparison (PE-1020).
 * `handUpload` is the record's own upload (PE-1023), whose undrawn outcomes are proposed copy.
 */
export default {
  cancel: {
    title: 'Cancel this payment?',
    confirm: 'Cancel payment',
    keep: 'Keep payment',
    summary: 'The payment being cancelled',
    payment: 'Payment',
    vendor: 'Vendor',
    cashAmount: 'Cash amount',
    requests: 'Payment requests',
    consequenceOne:
      'Cancelling returns its payment request to payable, so it can be paid again. Nothing financial is undone, because nothing moved while this payment waited for its proof. A cancellation cannot be reversed.',
    consequenceMany:
      'Cancelling returns its {count} payment requests to payable, so they can be paid again. Nothing financial is undone, because nothing moved while this payment waited for its proof. A cancellation cannot be reversed.',
    proofOne:
      'A proof is attached to this payment: {fileName}. Cancelling does not delete it. It is kept, no longer settles anything, and moves to Unmatched proofs, where it can be attached to another payment or rejected.',
    proofMany: '{count} proofs are attached to this payment:',
    proofManyConsequence:
      'Cancelling does not delete them. They are kept, no longer settle anything, and move to Unmatched proofs, where each can be attached to another payment or rejected.',
    reason: {
      label: 'Why are you cancelling this payment?',
      help: 'This is stored with the payment and shown on its record. It cannot be changed later.',
      required: 'A reason is required before this payment can be cancelled.',
    },
    notices: {
      notCancelled: 'This payment was not cancelled.',
      forbidden: 'You do not have permission to cancel payments. This payment was not cancelled.',
      notFound: 'This payment was not found, so it was not cancelled.',
      unknownAwaiting:
        'We could not confirm whether this payment was cancelled. It still shows as awaiting proof. You can try again: a payment cannot be cancelled twice.',
      unknownUnread:
        'We could not confirm whether this payment was cancelled, and the record could not be read. Try again, or close this and reopen the payment to see where it stands.',
      alreadyCancelled: 'This payment had already been cancelled, so nothing changed.',
      alreadyConfirmed:
        'This payment confirmed before it could be cancelled, so it was not cancelled. A confirmed payment cannot be cancelled.',
    },
    announcement: 'Payment {reference} cancelled. Its payment requests are payable again.',
  },
  eyebrow: 'Payment',
  openFromLog: 'Open payment {reference}',
  close: 'Close the payment record',
  context: {
    paymentsList: 'From the payments list',
    paymentRequest: 'From payment request {number}',
    transactionLog: 'From the account transaction log · {type}',
  },
  reference: {
    label: 'Payment reference',
    copyLabel: 'payment reference {reference}',
  },
  copyDetails: {
    label: 'Copy payment details',
    copied: 'Payment details copied',
    failed: 'The payment details could not be copied. Try again.',
    notOffered:
      "Nothing was copied: this payment's state has changed, so its details are no longer offered. The screen has been updated.",
    notRecorded: 'not recorded',
    lines: {
      beneficiary: 'Beneficiary',
      bank: 'Bank',
      branchCode: 'Branch code',
      accountNumber: 'Account number',
      amount: 'Amount',
      reference: 'Reference',
      proofAddress: 'Proof of payment email',
    },
  },
  status: {
    awaitingProof: 'Awaiting proof',
    blockedAtConfirm: 'Blocked at confirm',
    confirmed: 'Confirmed',
    confirmedNoCash: 'Confirmed · no cash',
    confirmedNoCashSpoken: 'Confirmed, no cash',
    cancelled: 'Cancelled',
    loading: 'Loading the status',
  },
  details: {
    title: 'Payment details',
    subtitle: 'What was paid, and to whom',
  },
  stats: {
    vendor: 'Vendor',
    unavailable: 'Unavailable',
    cashAmount: 'Cash amount',
    requestsSettled: 'Requests settled',
    creditApplied: 'Vendor credit applied',
  },
  loading: 'Loading the payment record',
  error: {
    title: 'This payment record could not be loaded',
    note: 'The payment itself is unaffected: this is a read that failed. Retrying reloads the whole record.',
    retry: 'Retry',
  },
  settled: {
    title: 'Settled these payment requests',
    subtitle: 'The requests this payment pays',
    note: 'Each of these requests links back here: its own Payment logged row points at this payment, so you can move between them in either direction.',
    due: 'Due {date}',
    back: 'Back to {reference}',
    loadingRequest: 'Loading the payment request',
    requestFailed: 'This payment request could not be loaded.',
  },
  proof: {
    title: 'Proof of payment',
    subtitle: 'The bank proof that this payment was made',
    zeroCash: {
      subtitle: 'Why there is no proof of payment',
      title: 'No proof of payment, and none is possible',
      body: {
        CoveredByCredit:
          'Vendor credit covered this payment in full, so no money was transferred and no proof of payment exists.',
        NoCashDue:
          'Nothing was due on this payment, so no money was transferred and no proof of payment exists.',
      },
    },
    storedLabel: 'Stored proof',
    blockedLabel: 'The blocked proof',
    meta: 'One proof for the whole payment · {type}',
    note: 'The document is never rendered here, only downloaded. A third-party scan cannot be assumed readable by assistive technology, so the account below is the decision surface rather than a fallback to it.',
    download: 'Download',
    downloadName: 'Download {fileName}, {type}',
    downloadNameWithSize: 'Download {fileName}, {type}, {size}',
    failed: 'The download could not be started. Try again in a moment.',
    unreachable: {
      title: 'Proof unreachable',
      note: 'The document store cannot be reached, so the file cannot be fetched. The confirmation below stands: it was made when the proof arrived, and an unreachable file does not undo it.',
    },
    none: {
      title: 'No proof stored yet',
      body: "The bank's proof of payment has not arrived. The transfer has to quote this payment's reference, and the bank has to email its proof to your proof address.",
      bodyEmailOff:
        "The bank's proof of payment has not arrived. The transfer has to quote this payment's reference.",
      referenceLabel: 'Reference',
      referenceDescription:
        'The bank transfer quotes this, so the proof can be matched to this payment.',
      proofAddressLabel: 'Proof address',
      proofAddressCopyLabel: 'proof address {address}',
      proofAddressDescription: 'The bank emails its proof of payment here.',
      proofAddressOff: 'Turned off',
      proofAddressOffDescription:
        'Proof of payment by email is off for this organisation, so an emailed proof will not close this payment. Only an uploaded proof can close it.',
    },
  },
  handUpload: {
    label: 'Upload proof of payment',
    note: 'The proof was said to be following by email, and it still can: uploading it here does not withdraw the address below. Whichever arrives first confirms the payment; the other is then an unmatched proof.',
    noteEmailOff:
      'The proof was said to be following by email, but proof of payment by email is now off for this organisation. Upload the proof here to confirm this payment.',
    chosenLabel: 'Chosen, not yet attached',
    intro: 'Attaching confirms this payment by hand.',
    consequence:
      'It will read {person} with your name against it, and no sender address, because nothing arrived by email. The amounts are still compared and a beneficiary that disagrees still blocks the confirmation: attaching by hand skips the wait, not the checks.',
    consequencePerson: 'confirmed by a person',
    comparing: 'Checking the amount and the beneficiary on this proof against this payment.',
    attach: 'Attach proof',
    footer: {
      chosen:
        "Attaching confirms this payment by hand. The proof address stays live: if the bank's email arrives first, that confirms it instead.",
      notAttached: 'Nothing was attached. The proof address stays live.',
      chosenEmailOff: 'Attaching confirms this payment by hand.',
      notAttachedEmailOff: 'Nothing was attached.',
    },
    blocked: {
      disagrees:
        "Not attached. The amount on this proof does not agree with this payment's cash amount.",
      unreadable:
        'Not attached. No amount could be read from this proof, so it could not be compared with this payment. It may be a poor scan.',
      fileType:
        'Not attached. This file is a {type} file, which cannot be read, so its amount could not be compared. Upload a PDF, JPG or PNG, or confirm on it with a reason.',
      fileTypeUnnamed:
        'Not attached. This file cannot be read, so its amount could not be compared. Upload a PDF, JPG or PNG, or confirm on it with a reason.',
      fileTypeNoOverride:
        'Not attached. This file is a {type} file, which cannot be read, so its amount could not be compared. Upload a PDF, JPG or PNG.',
      fileTypeUnnamedNoOverride:
        'Not attached. This file cannot be read, so its amount could not be compared. Upload a PDF, JPG or PNG.',
      notChecked:
        'Not attached. This proof could not be checked: reading documents is unavailable just now, so no amount was compared.',
    },
    next: {
      disagrees: 'You can confirm on this amount with a reason, or replace the file.',
      noAmount: 'You can confirm on it with a reason, or replace the file.',
    },
    beneficiary: {
      fits: 'Beneficiary on the proof: {name}. It fits the vendor.',
      notJudged: 'Beneficiary on the proof: {name}. It neither fits nor contradicts the vendor.',
      noName: 'No beneficiary name could be read, so it could not be judged.',
    },
    refused: {
      otherVendor:
        'Not attached. This proof is for a different beneficiary, so it cannot confirm this payment, and this cannot be overridden.',
      otherVendorLine:
        'Nothing was kept, and nothing was added to Unmatched proofs. Check you chose the right document, or upload the proof for this payment.',
      beneficiaryOnProof: 'Beneficiary on the proof',
      paymentVendor: "This payment's vendor",
      notRecorded: 'Not recorded',
      severalPayments:
        'This document appears to cover more than one payment. Upload the proof for this payment alone.',
      severalPaymentsLine: 'Nothing was kept, and this cannot be overridden.',
      heldAsPaymentProof:
        'This document is already held as the proof of {reference}, so it cannot confirm this payment. Nothing was attached.',
      heldAsUnmatchedProof:
        'This document is already on Unmatched proofs. Attach it to this payment from there instead. Nothing was attached here.',
      heldAsUnmatchedProofWhileUnknown:
        'This document is already on Unmatched proofs. Attach it to this payment from there instead.',
      heldAsRejectedProof:
        'This document was rejected earlier, so it cannot confirm this payment. Nothing was attached.',
      heldAsRejectedProofWhileUnknown:
        'This document was rejected earlier, so it cannot confirm this payment.',
      heldElsewhere:
        'This document is already held, so it cannot confirm this payment. Nothing was attached.',
      heldElsewhereWhileUnknown:
        'This document is already held, so it cannot confirm this payment.',
      heldPaymentLabel: 'Already held as the proof of',
      heldPaymentCopyLabel: 'payment reference {reference}',
      returnChoiceMissing:
        'This payment owes a return choice that was never recorded, so a proof cannot confirm it. Nothing was attached.',
    },
    notice: {
      attachFailed:
        'We could not confirm whether the proof was attached. The record shows this payment as it stands now.',
      paymentChanged:
        'Nothing was attached. This payment changed before your proof could confirm it.',
      gateFailed:
        'Your proof was kept, but the payment did not confirm: {clause}. The proof is now on Unmatched proofs as Blocked at confirm.',
      gateFailedUnnamed:
        'Your proof was kept, but the payment did not confirm. The proof is now on Unmatched proofs as Blocked at confirm.',
      alreadySettled:
        'Another proof confirmed this payment first. The document you uploaded is now on unmatched proofs.',
    },
    gateClause: {
      RequestsChanged: 'its payment requests changed after it was created',
      TotalChanged: 'its requests no longer total the payment amount',
      CreditUnavailable: 'the credit it applied is no longer available',
    },
    override: {
      action: 'Override with a reason',
      title: 'Confirm on a disagreeing amount',
      backLabel: 'Back to payment {reference}',
      back: 'Back',
      confirm: 'Override and confirm',
      warn: 'Confirming records this payment as Confirmed on a disagreeing amount. Your reason, your name ({name}) and these figures are kept on the payment record and shown on the payments list. A confirmed payment cannot be reversed.',
      warnUnnamed:
        'Confirming records this payment as Confirmed on a disagreeing amount. Your reason, your name and these figures are kept on the payment record and shown on the payments list. A confirmed payment cannot be reversed.',
      warnNoAmount:
        'Confirming records this payment as Confirmed on a disagreeing amount. No amount could be read from the proof. Your reason and your name ({name}) are kept on the payment record and shown on the payments list. A confirmed payment cannot be reversed.',
      warnNoAmountUnnamed:
        'Confirming records this payment as Confirmed on a disagreeing amount. No amount could be read from the proof. Your reason and your name are kept on the payment record and shown on the payments list. A confirmed payment cannot be reversed.',
      reasonLabel: 'Why are you confirming on this amount?',
      reasonHelp:
        'For example: the bank deducted a fee in transit, and the vendor confirmed what it received.',
    },
  },
  confirmation: {
    title: 'How this payment confirmed',
    subtitle: 'How the proof matched and who confirmed it',
    subtitleZeroCash: 'Who confirmed it, and why',
    terms: {
      reason: 'Reason',
      confirmedBy: 'Confirmed by',
      confirmedAt: 'Confirmed at',
      attemptedAt: 'Attempted at',
      matchedOn: 'Matched on',
      amountsCompared: 'Amounts compared',
      beneficiary: 'Beneficiary',
      senderAddress: 'Sender address',
      checkThatFailed: 'Check that failed',
      amountCheckOverridden: 'Amount check overridden',
    },
    at: '{date} at {time}',
    actor: {
      system: 'system',
      systemSub:
        'An automatic confirmation. Not the person who created the payment: nobody confirmed this by hand.',
      zeroCashSub: 'An automatic confirmation at submission, with nothing to wait for.',
      formerUser: 'A former user',
      notRecorded: 'Not recorded',
      notRecordedSub: 'No record was kept of who logged this payment.',
      atCreationSub:
        'The person who logged the payment. The proof was uploaded as it was logged, so it confirmed at once.',
      notConfirmed: 'Not confirmed',
    },
    zeroCash: {
      reason: {
        CoveredByCredit: 'Covered entirely by vendor credit',
        NoCashDue: 'Nothing was due',
        notRecorded: 'Not recorded',
      },
      reasonSub:
        'No cash moved, so there is no transfer, no proof of payment and nothing to match. There is no rung and no beneficiary comparison, because there was no bank instruction to compare against.',
    },
    matchedOn: {
      reference: 'Payment reference',
      referenceSub: "The proof quoted this payment's reference.",
      amount: 'Amount alone',
      amountSub:
        'No reference on the proof resolved to this payment, so it was matched on its amount alone.',
      attached: 'Proof attached by a person',
      attachedEmailSub:
        'No automatic rung was used: a person attached this emailed proof to the payment.',
      attachedUploadSub:
        'No automatic rung was used: the document was uploaded against this payment directly.',
      atCreation: 'Proof uploaded when the payment was logged',
    },
    compared: {
      notCompared: 'Not compared',
      notComparedSub: 'This proof was uploaded before uploaded proofs were read.',
      couldNotBeChecked: 'Could not be checked',
      couldNotBeCheckedSub:
        'The proof could not be read when the payment was logged, so the two amounts were not compared.',
    },
    beneficiary: {
      Fits: "Fits the vendor's name",
      DoesNotFit: "Does not fit the vendor's name",
      CouldNotBeJudged: 'Could not be judged',
      nameOnProof: '{name} on the proof.',
      couldNotBeJudgedSub:
        "The name on the proof neither fits nor contradicts the vendor's name, so it did not decide the match.",
      noNameSub: 'No beneficiary name was read from the proof.',
    },
    sender: {
      emailedSub: 'The address this proof was emailed from.',
      notApplicable: 'Not applicable',
      uploadedSub:
        'Nothing arrived by email: the proof was uploaded, so there is no sending address to attribute it to.',
    },
    check: {
      RequestsChanged: 'Its payment requests changed after it was created',
      TotalChanged: 'Its requests no longer total the payment amount',
      CreditUnavailable: 'The credit it applied is no longer available',
      sub: 'This is the check that failed.',
      notRecorded: 'Not recorded',
    },
    overridden: {
      by: 'By {name}, {at}',
      reason: 'Reason given: {reason}',
    },
    noRecord:
      'Nothing has confirmed this payment, so there is nothing to account for here. This region fills the moment a proof matches. It is never pre-filled with a pending placeholder.',
    disclaimer:
      "BOQNET's match is not a certification that this payment was correctly made. Reconciling it remains your organisation's responsibility.",
  },
  flags: {
    MatchedOnAmountAlone: 'Matched on amount alone',
    PastMatchingWindow: 'Past the 30-day matching window',
    ManualAmountOverride: 'Confirmed on a disagreeing amount',
    listLabel: 'Flags on this payment',
    confirmedPrefix: 'Information on a confirmed payment:',
  },
  comparison: {
    proofFigure: '{amount} on the proof',
    proofUnavailable: 'unavailable on the proof',
    cashFigure: "{amount}, the payment's cash amount ({reference})",
    cashFigureUnreferenced: "{amount}, the payment's cash amount",
    unavailable: 'unavailable',
    Equal: 'They agree to the cent.',
    ProofHigher: '{amount} more on the proof than on the payment.',
    ProofLower: '{amount} less on the proof than on the payment.',
    noProofAmount:
      'No amount could be read from this document, so no difference is computed and none is implied.',
    captioned: {
      caption: 'From comparing this proof with the payment',
      proofSays: 'The proof says',
      paymentCash: "The payment's cash amount ({reference})",
      paymentCashUnreferenced: "The payment's cash amount",
      difference: 'Difference',
      unavailable: 'unavailable',
      notComputed: 'Not computed',
      ProofLower: '{amount} short',
      ProofHigher: '{amount} over',
      Equal: 'They agree to the cent.',
    },
  },
  // Context: the Cancelled outcome's text is its own namespace because a payment that never paid
  // cannot say "settled" or "confirmed" (DL-209); every string is proposed copy (DL-146) except
  // the design's five terms and the orphaned proof region.
  cancelled: {
    detailsSubtitle: 'What was to be paid, and to whom',
    requestsLabel: 'Requests',
    proofSubtitle: 'What happened to any attached proof',
    requests: {
      title: 'Payment requests on this payment',
      subtitle: 'The requests this payment would have paid',
    },
    terms: {
      title: 'How this payment was cancelled',
      subtitle: 'Who cancelled it, when and why',
      cancelledBy: 'Cancelled by',
      cancelledAt: 'Cancelled at',
      reasonCaptured: 'Reason captured',
      attachedProof: 'Attached proof',
      requestsReleased: 'Requests released',
    },
    reasonSub: 'The reason is the one entered at cancellation, not a generated one.',
    attached: {
      one: 'Orphaned and retained',
      oneSub:
        'The document that was attached is kept and no longer settles anything. It is never deleted: it appears under Unmatched proofs.',
      several: '{count} orphaned and retained',
      severalSub:
        'The documents that were attached are kept and no longer settle anything. They are never deleted: they appear under Unmatched proofs.',
      none: 'None',
      noneSub: 'No proof was attached when this payment was cancelled, so nothing was orphaned.',
    },
    released: {
      all: 'All {count} returned to payable',
      some: '{count} returned to payable',
    },
    proof: {
      orphanedTitle: 'The proof was orphaned, not deleted',
      orphanedBody:
        'The document attached before cancellation is retained and no longer settles anything. It is listed under Unmatched proofs, where it can be matched to another payment.',
      orphanedTitlePlural: 'The proofs were orphaned, not deleted',
      orphanedBodyPlural:
        'The documents attached before cancellation are retained and no longer settle anything. They are listed under Unmatched proofs, where each can be matched to another payment.',
      orphanedListLabel: 'Orphaned proofs',
      noFileName: 'A proof with no stored document',
      noneTitle: 'No proof was attached',
      noneBody:
        'No proof had arrived for this payment when it was cancelled, so nothing was orphaned. A proof that arrives for it now cannot settle it, and is listed under Unmatched proofs.',
    },
  },
  attachmentLine: {
    replace: 'Replace',
    remove: 'Remove',
    replaceLabel: 'Replace {name}',
    removeLabel: 'Remove {name}',
  },
};
